import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppStateType } from '../../reducers/appstate'
import { withRouter } from 'next/router'
import AccountManagement from '@components/account/AccountManagement'
import { FlexCol, Header } from './index'
import styled from 'styled-components'
import {
  colors,
  CTAPrimary,
  GoodSansB2,
  GoodSansH1, GoodSansLink
} from '@components/shared'
import type { RefundObjectType, SubscriptionType } from '../../flowTypes'
import { LinkWithUtms } from '../../actions/navigationActions'
import { startCase } from 'lodash'
import { humanReadableDate } from '@helpers/dateHelper'
import { loadUserAsync } from '../../actions/userActions'
import { getManagementUrlAsync } from '@services/revenuecat'

function SubscriptionDetails (): React.ReactNode {
  const dispatch = useDispatch()
  const isSubscribed = useSelector((state: AppStateType): boolean => !!state.user?.isSubscribed)
  const subscriptionLength = useSelector((state: AppStateType): 'monthly' | 'annual' | undefined => state.user?.subscription_length)
  const subscriptionType = useSelector((state: AppStateType): SubscriptionType | null | undefined => state.user?.subscription_type)
  const subscriptionDate = useSelector((state: AppStateType): number | null | undefined => state.user?.subscribed_at)
  const subscriptionRenewalDate = useSelector((state: AppStateType): number | null | undefined => state.user?.subscription_renews_at)
  const subscriptionExpiresDate = useSelector((state: AppStateType): number | null | undefined => state.user?.subscription_expires_at)
  const last4 = useSelector((state: AppStateType): string | null | undefined => state.subscriptionPurchase?.last4)
  const refund = useSelector((state: AppStateType): RefundObjectType | null | undefined => state.refund)
  const [managementUrl, setManagementUrl] = React.useState<string | null>(null)

  React.useEffect((): void => {
    dispatch(loadUserAsync())
    if (managementUrl === null) {
      getManagementUrlAsync().then((url: string | null): void => setManagementUrl(url))
    }
  }, [isSubscribed])

  const renderNoSub = (): React.ReactNode => {
    if (!isSubscribed) {
      return (<>
        <Body style={{ marginBottom: '32px' }}>You are not currently subscribed to Dipsea.</Body>
        <LinkWithUtms pathname={'/account'}>
          <CTAPrimary>BACK TO ACCOUNT</CTAPrimary>
        </LinkWithUtms>
      </>
      )
    }
    return null
  }

  const renderRefundLink = (): React.ReactNode => {
    if (refund && refund.status === 'eligible') {
      return (
        <SectionContainer>
          <SubHeader>30-Day Money Back Refund</SubHeader>
          <SubsectionContainer>
            <Body>
              You are eligible for a 30-day money back refund.
            </Body>
            <LinkWithUtms pathname={'/account/refund'}>
              <Link>REFUND</Link>
            </LinkWithUtms>
          </SubsectionContainer>
        </SectionContainer>
      )
    }
    return null
  }

  const renderSubLength = (): React.ReactNode => {
    if (subscriptionLength) {
      return (<SectionContainer>
        <SubHeader>Subscription type</SubHeader>
        <SubsectionContainer>
          <Body>{startCase(subscriptionLength)}</Body>
        </SubsectionContainer>
      </SectionContainer>)
    }
    return null
  }

  const renderSubType = (): React.ReactNode => {
    if (subscriptionType) {
      return (<SectionContainer>
        <SubHeader>Subscription via</SubHeader>
        <SubsectionContainer>
          {subscriptionType === 'gift' && <Body>Gift Card</Body>}
          {subscriptionType === 'stripe' && <Body>Web</Body>}
          {subscriptionType === 'rc_billing' && <Body>Web</Body>}
          {subscriptionType === 'ios' && <Body>Apple App Store</Body>}
          {subscriptionType === 'android' && <Body>Google Play Store</Body>}
        </SubsectionContainer>
      </SectionContainer>)
    }
    return null
  }

  const renderStart = (): React.ReactNode => {
    if (subscriptionDate) {
      return (<SectionContainer>
        <SubHeader>Start date</SubHeader>
        <SubsectionContainer>
          <Body>{humanReadableDate(subscriptionDate)}</Body>
        </SubsectionContainer>
      </SectionContainer>)
    }
    return null
  }

  const renderRenewalDate = (): React.ReactNode => {
    if (subscriptionRenewalDate && !subscriptionExpiresDate) {
      return (<SectionContainer>
        <SubHeader>Renewal date</SubHeader>
        <SubsectionContainer>
          <Body>{humanReadableDate(subscriptionRenewalDate)}</Body>
          {subscriptionType === 'stripe' && <LinkWithUtms pathname={'/account/cancel'}>
            <Link>CANCEL</Link>
          </LinkWithUtms>}
          {(subscriptionType === 'rc_billing' && managementUrl) && <>
            <Link href={managementUrl} target="_blank">CANCEL</Link>
          </>}
        </SubsectionContainer>
      </SectionContainer>)
    }
    return null
  }

  const renderExpirationDate = (): React.ReactNode => {
    if (subscriptionExpiresDate) {
      return (<SectionContainer>
        <SubHeader>Expiration date</SubHeader>
        <SubsectionContainer>
          <Body>{humanReadableDate(subscriptionExpiresDate)}</Body>
        </SubsectionContainer>
      </SectionContainer>)
    }
    return null
  }

  const renderPaymentMethod = (): React.ReactNode => {
    if (subscriptionType) {
      return (<SectionContainer>
        <SubHeader>Payment method</SubHeader>
        <SubsectionContainer>
          {last4 && <><Body>Card {last4}</Body><LinkWithUtms pathname={'/account/update-card'}>
            <Link>UPDATE CARD</Link>
          </LinkWithUtms></>}
          {(subscriptionType === 'rc_billing' && managementUrl) && <>
            <Link href={managementUrl} target="_blank">UPDATE CARD</Link>
          </>}
          {subscriptionType === 'gift' && <Body>Gift Card</Body>}
          {subscriptionType === 'ios' && <Body>
            You purchased your subscription in the Apple App Store. More details on how to manage your subscription <LinkWithUtms pathname={'https://support.dipseastories.com/en/articles/5433433-how-do-i-manage-my-subscription'}>here.</LinkWithUtms>
          </Body>}
          {subscriptionType === 'android' && <Body> You purchased your subscription in the Google Play Store. More details on how to manage your subscription <LinkWithUtms pathname={'https://support.dipseastories.com/en/articles/5433433-how-do-i-manage-my-subscription'}>here.</LinkWithUtms></Body>}
        </SubsectionContainer>
      </SectionContainer>)
    }
    return null
  }

  return (
      <AccountManagement title={'Dipsea | Manage Account | Subscription'} pageDescription={'Dipsea Account Management'} breadCrumbTitle={'Subscription Details'}>
        <FlexCol>
          <Header>Subscription Details</Header>
          <SectionsContainer>
            {renderNoSub()}
            {renderSubLength()}
            {renderSubType()}
            {renderStart()}
            {renderRenewalDate()}
            {renderExpirationDate()}
            {renderPaymentMethod()}
            {renderRefundLink()}
          </SectionsContainer>
        </FlexCol>
      </AccountManagement>
  )
}

export default withRouter(SubscriptionDetails as any) as any

const SectionsContainer = styled.div<any>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const SectionContainer = styled.div<any>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding-bottom: 16px;
  margin-bottom: 16px;

  ${(props: any): string => {
  if (props.border === false) {
    return 'border-bottom: none;'
  }
  return `border-bottom: 1px solid ${colors.white70}`
}}
`

const SubsectionContainer = styled.div<any>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`

const SubHeader = styled.p<any>`
  ${GoodSansH1};
  margin-bottom: 10px;
`

const Body = styled.p<any>`
  ${GoodSansB2};
  color: ${colors.white70};
  a {
    color: ${colors.tintOrange};
  }
`
const Link = styled.a<any>`
  ${GoodSansLink};
`
